import { Quaternion } from 'three';

import {
  SAM2_SHADOW_MARGIN_FRACTION,
  bytesToBase64,
  cosineSimilarityHeatmap,
  fitOrientedBlobs,
  fp16ToFp32,
  fp32ArrayToFp16Bytes,
  isUnboundedMaxMatchArea,
  nonMaxSuppression,
  PatchTensor,
  rasterizeHeatmapFp16,
  thresholdAndConnectedComponents,
  uint8BytesToUint16,
  unletterboxBlob,
  unletterboxOrientedBlob,
  derivePointPrompts,
  deriveHeatmapSamples,
  DEFAULT_NEGATIVE_WEIGHT,
  isUnpinnedModelVersion,
  CLICKFIND_ERROR_MESSAGES,
} from '@sb/dinofast';
import type { CartesianPose, Plane } from '@sb/geometry';
import {
  applyCompoundPose,
  getPlanePose,
  invertPose,
  movePlaneInItsZDirection,
  ZERO_POSE,
} from '@sb/geometry';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import type { GPUTaskRunner } from '@sb/integrations/implementations/GPUTaskRunner/implementation/GPUTaskRunner';
import type { WristCamera } from '@sb/integrations/implementations/WristCamera/implementation/WristCamera';
import type { WristCameraConfiguration } from '@sb/integrations/implementations/WristCamera/types';
import type {
  CameraIntegration,
  CameraIntrinsics,
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';
import { makeNamespacedLog } from '@sb/log';
import {
  ExtrinsicsKind,
  GraspFrame,
  LocateHeadKind,
  PoseEstimationMethod,
  PrimitiveShapeType,
} from '@sb/messenger/auto_generated/messenger/vision_bot_service';
import type {
  Blob2D as ProtoBlob2D,
  RunPlaneProjectionRequest,
  Run3DPoseEstimationRequest,
  CameraParams,
  Detection3D,
  RunCheckpointBasedInferenceResponse as ProtoInferenceResponse,
  ComputeDinoEmbeddingsResponse,
  ComputeDinoImageEmbeddingResponse,
  LocateHeadProto,
  SegmentMaskResponse,
  TrainLocateHeadResponse,
} from '@sb/messenger/auto_generated/messenger/vision_bot_service';
import type {
  LocateMethod,
  RegionOfInterest,
  RoutineRunnerState,
  VisionInterface,
  Space,
  EquipmentInterface,
} from '@sb/routine-runner';
import {
  DEFAULT_RANSAC_DISTANCE_THRESHOLD,
  DEFAULT_RANSAC_MAX_ITERATIONS,
  DEFAULT_RANSAC_MIN_SAMPLES,
  type PlaneDetectionModeType,
} from '@sb/routine-runner/Step/Locate/Arguments';
import { isNotUndefined } from '@sb/utilities';

import { contourArea } from './blobGeometry';
import { getCalibrationOffset } from './calibrationHelpers';
import {
  applyDisableRotation,
  getCameraColorFrame,
  resolveVisionExtrinsics,
} from './helpers';
import type {
  Blob2D,
  Detection2D,
  DinoFastLocateHead,
  DinoFastParams,
  LocateMatch,
  LocateResult,
  ResultOrdering,
  TextPromptDetection2DParams,
} from './LocateTypes';
import {
  RunCheckpointBasedInferenceResponse,
  RunRoboflowDetectionResponse,
} from './LocateTypes';
import { poseToDisplayFrame } from './poseToDisplayFrame';
import { sortBlobs } from './resultOrdering';
import type { Detection3DPoseEstimationConfig } from './visionConfigSchemas';
import type {
  DinoFastDenseHeatmap,
  OrientationReferenceRejection,
} from './VisionMethodRunnerTypes';

const log = makeNamespacedLog('runLocate');

/**
 * We allow users to define a tranform to offset locate results by a set amount relative to the plane.
 * This applies that transform to a blob
 */
function applyUserDefinedTransform(
  transform: CartesianPose,
  blobRelativeToBase: CartesianPose,
  planePoseRelativeToBase: CartesianPose,
): CartesianPose {
  // The user-defined tranform is relative to the plane, so get the
  // blobs in plane coordinates first, then apply the transform
  // and then transform back to base coordinates
  const blobRelativeToPlane = applyCompoundPose(
    blobRelativeToBase,
    invertPose(planePoseRelativeToBase),
  );

  // apply the rotation separately so it doesn't change the position
  const rotation = new Quaternion(
    blobRelativeToPlane.i,
    blobRelativeToPlane.j,
    blobRelativeToPlane.k,
    blobRelativeToPlane.w,
  );

  const offsetRotation = new Quaternion(
    transform.i,
    transform.j,
    transform.k,
    transform.w,
  );

  const newRotation = offsetRotation.multiply(rotation);

  const offsetBlob = {
    x: blobRelativeToPlane.x + transform.x,
    y: blobRelativeToPlane.y + transform.y,
    z: blobRelativeToPlane.z + transform.z,
    i: newRotation.x,
    j: newRotation.y,
    k: newRotation.z,
    w: newRotation.w,
  };

  const offsetBlobRelativeToBase = applyCompoundPose(
    offsetBlob,
    planePoseRelativeToBase,
  );

  return offsetBlobRelativeToBase;
}

function getCalibrationCorrection(
  pose: CartesianPose,
  calibration: Space.AccuracyCalibrationEntry[],
): CartesianPose {
  if (calibration.length === 0) {
    log.info('getCalibrationCorrection.none', 'No calibration data found');

    return ZERO_POSE;
  }

  const calibrationOffset = getCalibrationOffset(pose, calibration);

  log.info(
    'getCalibrationCorrection.offset',
    'Calibration offset',
    calibrationOffset,
  );

  return calibrationOffset;
}

interface RunLocateArgs {
  camera: {
    equipment: Camera | WristCamera | undefined;
    integration: CameraIntegration | undefined;
  };
  regionOfInterest: RegionOfInterest;
  method: LocateMethod;
  plane?: Plane;
  resultsLimit: number | undefined;
  resultOrdering?: ResultOrdering;
  transform: CartesianPose | null;
  /**
   * Grasp frame for the locate result: defines the base in-plane orientation
   * of the returned pose. Sent to vision-bot as the `grasp_frame` field on the
   * plane-projection request, where vision-bot resolves the finished pose
   * orientation:
   *   - `'object'` / `undefined` → the detection principal axis (default).
   *   - `'plane'` → the taught plane X axis.
   *   - `'world'` → the world X axis projected onto the plane.
   * The user-defined `transform` offset always rides in the returned pose's own
   * display frame, regardless of grasp frame.
   */
  graspFrame?: 'object' | 'plane' | 'world';
  /**
   * Canonical orientation frame from teach time, when the taught shape
   * supported a reliable, non-symmetric frame. Rides into the plane-projection
   * request's `orientation_reference` field; vision-bot registers each detected
   * contour against it (object grasp frame) to resolve the 180° flip. Null/
   * undefined ⇒ the field is left unset and vision-bot falls back to plane.
   */
  orientationReference?: { referenceContour: number[][] } | null;
  accuracyCalibration: Space.AccuracyCalibrationEntry[] | null;
  vision: VisionInterface;
  objectHeight: number;
  getState: () => RoutineRunnerState;
  equipment: EquipmentInterface;
  intrinsics: CameraIntrinsics | CameraIntrinsicsExtended | undefined;
  extrinsics: ExtrinsicsKinematics | undefined;
  gpuTaskRunner?: GPUTaskRunner;
  planeDetectionMode?: PlaneDetectionModeType;
  ransacParams?: {
    maxIterations?: number;
    distanceThreshold?: number;
    minSamples?: number;
  };
  depthImage?: ArrayBuffer;
  includeDebugVisualization?: boolean;
  maxTiltAngle?: number;
  disablePlaneOrientation?: boolean;
  /**
   * Projection mode for 2D detection methods. 'Manual' (default/undefined) =
   * project onto the taught plane. 'Detection3D' = estimate a 6D pose per
   * detection from depth via the Detection3D backend (requires depthImage +
   * detection3dConfig). Distinct from method.kind === 'Detection3D', which is
   * the routine Locate step's own text-prompt 3D method.
   */
  projectionMode?: 'Manual' | 'Detection3D';
  /**
   * Pose-estimation config for projectionMode 'Detection3D'. Required when
   * projectionMode === 'Detection3D' (run2DMethodWith3DProjection throws if
   * absent).
   */
  detection3dConfig?: Detection3DPoseEstimationConfig;
  /**
   * DinoFast preview only: also return the dense score heatmap from the 2D
   * detection pass (teach-preview overlay), so the routine editor's ClickFind
   * Locate step renders the same heatmap as the Teach page. Default/undefined ⇒
   * the heatmap is not computed-for-return (routine execution path).
   */
  returnHeatmap?: boolean;
}

/**
 * Shift a LocateMatch from cropped-ROI space back into full-frame space. The
 * detector box center (`detection.x/y`) AND, when a segment is present, every
 * `segment.contour` point are in ROI-local coords after detecting on a cropped
 * image — both must be translated by `+offsetX/+offsetY` so the match stays in
 * full-frame coordinates. The segment's RLE mask (if any) is left untouched:
 * downstream consumers only read the contour from a producer's segment.
 */
function shiftMatchByOffset(
  match: LocateMatch,
  offsetX: number,
  offsetY: number,
): LocateMatch {
  const shiftedSegment = match.segment
    ? {
        ...match.segment,
        contour: match.segment.contour.map((p) => ({
          x: p.x + offsetX,
          y: p.y + offsetY,
        })),
      }
    : null;

  return {
    detection: {
      ...match.detection,
      x: match.detection.x + offsetX,
      y: match.detection.y + offsetY,
    },
    segment: shiftedSegment,
  };
}

export async function getCustomCheckpointBlobs({
  image,
  regionOfInterest,
  method,
  equipment,
  vision,
}: {
  image: ArrayBuffer;
  regionOfInterest: RegionOfInterest;
  method: LocateMethod;
  equipment: EquipmentInterface;
  vision: VisionInterface;
}): Promise<LocateMatch[]> {
  if (method.kind !== 'CustomCheckpointDetection2D') {
    throw new Error('Expected CustomCheckpointDetection2D method');
  }

  // Get the GPUTaskRunner equipment
  const gpuTaskRunner = equipment.getDeviceById(
    method.settings.gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${method.settings.gpuTaskRunnerId}`,
    );
  }

  // Fail fast if GPU's vision bot is not connected
  if (!gpuTaskRunner.update().isVisionBotConnected) {
    throw new Error(
      `GPU Task Runner '${method.settings.gpuTaskRunnerId}' is not healthy (vision bot disconnected). Custom checkpoint detection requires a connected GPU Task Runner.`,
    );
  }

  // Preprocess the image using the vision service for ROI cropping
  const { croppedImage, offsetX, offsetY } = await vision.preprocessImage(
    image,
    regionOfInterest,
  );

  // Convert cropped image to base64
  const base64Image = Buffer.from(croppedImage).toString('base64');

  // A Roboflow-hosted model runs via the dedicated RunRoboflowDetection RPC
  // (api_key injected robot-side); it returns LocateMatch[] like checkpoint
  // inference, so it flows through the same shift + plane-projection path below.
  if (method.settings.modelSource === 'roboflow') {
    if (!method.settings.roboflowId) {
      throw new Error(
        'Roboflow model source selected but no model was chosen.',
      );
    }

    // `use_sam` is dropped rather than forwarded: this path never runs SAM2, so
    // sending it would have the request claim segmentation that never happens.
    // Stripped here rather than at the UI so skills saved before the switch was
    // hidden don't keep carrying it. The remaining keys (confidence_threshold,
    // nms_threshold, max_detections) are the ones vision-bot actually reads.
    const { use_sam: _unusedUseSam, ...roboflowThresholdingParams } =
      method.settings.thresholdingParams;

    const roboflowResponse = await gpuTaskRunner.executeVisionCommand({
      command: 'run_roboflow_detection',
      image: base64Image,
      modelUrl: method.settings.roboflowId,
      thresholding_params: roboflowThresholdingParams,
    });

    const roboflowParse =
      RunRoboflowDetectionResponse.safeParse(roboflowResponse);

    if (!roboflowParse.success) {
      log.error(
        'getCustomCheckpointBlobs',
        'Roboflow detection returned invalid response format',
        {
          response: roboflowResponse,
          parseError: roboflowParse.error.issues,
        },
      );

      throw new Error(
        `Roboflow detection returned invalid response format: ${roboflowParse.error.issues
          .map((i: any) => i.message)
          .join(', ')}`,
      );
    }

    if (roboflowParse.data.error) {
      log.error(
        'getCustomCheckpointBlobs',
        `Roboflow detection failed: ${roboflowParse.data.error.message}`,
        { errorCode: roboflowParse.data.error.code },
      );

      throw new Error(
        `Roboflow detection failed: ${roboflowParse.data.error.message}`,
      );
    }

    // Both oneof arms unset means something is wrong upstream — a vision-bot
    // that predates the RPC, or a handler that returned without setting either.
    // Treating it as "found nothing" would surface later as a misleading
    // "object not found" instead of the actual RPC problem, so fail like the
    // checkpoint path below does.
    if (!roboflowParse.data.data) {
      throw new Error('Unexpected response type after validation');
    }

    const { blobs: roboflowBlobs, processingTimeMs } = roboflowParse.data.data;

    log.info(
      'getCustomCheckpointBlobs',
      `Roboflow detection completed successfully, found ${roboflowBlobs.length} blobs`,
      {
        processingTimeMs,
        modelUrl: method.settings.roboflowId,
      },
    );

    return roboflowBlobs.map((match) =>
      shiftMatchByOffset(match, offsetX, offsetY),
    );
  }

  // Execute the RunCheckpointBasedInference command
  const response = await gpuTaskRunner.executeVisionCommand({
    command: 'run_inference',
    image: base64Image,
    model_path: method.settings.modelPath,
    model_type: method.settings.modelType,
    thresholding_params: method.settings.thresholdingParams,
  });

  // Parse and validate the response using Zod schema
  const parseResult = RunCheckpointBasedInferenceResponse.safeParse(response);

  if (!parseResult.success) {
    log.error(
      'getCustomCheckpointBlobs',
      'Custom checkpoint inference returned invalid response format',
      {
        response,
        parseError: parseResult.error.issues,
      },
    );

    throw new Error(
      `Custom checkpoint inference returned invalid response format: ${parseResult.error.issues.map((i: any) => i.message).join(', ')}`,
    );
  }

  const validatedResponse = parseResult.data;

  // Handle error response
  if (validatedResponse.error) {
    log.error(
      'getCustomCheckpointBlobs',
      `Custom checkpoint inference failed: ${validatedResponse.error.message}`,
      { errorCode: validatedResponse.error.code },
    );

    throw new Error(
      `Custom checkpoint inference failed: ${validatedResponse.error.message}`,
    );
  }

  // Handle successful inference result
  if (validatedResponse.data) {
    const inferenceResult = validatedResponse.data;
    const { blobs } = inferenceResult;

    log.info(
      'getCustomCheckpointBlobs',
      `Custom checkpoint inference completed successfully, found ${blobs.length} blobs`,
      {
        processingTimeMs: inferenceResult.processingTimeMs,
        modelPath: inferenceResult.modelPath,
      },
    );

    // The proto blobs are LocateMatch (detector box + optional SAM2 segment).
    // Shift each back to original-image space: the detection box center and the
    // segment contour points both live in the cropped-ROI frame.
    return blobs.map((match) => shiftMatchByOffset(match, offsetX, offsetY));
  }

  throw new Error('Unexpected response type after validation');
}

export async function getTextPromptBlobs({
  image,
  regionOfInterest,
  params,
  equipment,
  vision,
}: {
  image: ArrayBuffer;
  regionOfInterest: RegionOfInterest;
  params: TextPromptDetection2DParams;
  equipment: EquipmentInterface;
  vision: VisionInterface;
}): Promise<LocateMatch[]> {
  // If no GPU Task Runner is specified, use the vision service directly
  if (!params.gpuTaskRunnerId) {
    return vision.detect2DTextPrompt(image, regionOfInterest, params);
  }

  // Get the GPUTaskRunner equipment
  const gpuTaskRunner = equipment.getDeviceById(
    params.gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${params.gpuTaskRunnerId}`,
    );
  }

  // Fall back to local vision if GPU's vision bot is not connected
  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'getTextPromptBlobs',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.detect2DTextPrompt(image, regionOfInterest, params);
  }

  // Preprocess the image using the vision service for ROI cropping
  const { croppedImage, offsetX, offsetY } = await vision.preprocessImage(
    image,
    regionOfInterest,
  );

  // Convert cropped image to base64
  const base64Image = Buffer.from(croppedImage).toString('base64');

  // Execute the text prompt detection via GPU Task Runner
  const response = await gpuTaskRunner.executeVisionCommand({
    command: 'run_inference',
    image: base64Image,
    model_path: 'text_prompt',
    model_type: 'TextPrompt',
    thresholding_params: {
      prompt: params.textInput,
      use_sam: params.useSam.toString(),
      box_threshold: params.confidenceThreshold.toString(),
      text_threshold: params.confidenceThreshold.toString(),
    },
  });

  const parseResult = RunCheckpointBasedInferenceResponse.safeParse(response);

  if (!parseResult.success) {
    log.error(
      'getTextPromptBlobs',
      'Text prompt detection returned invalid response format',
      {
        response,
        parseError: parseResult.error.issues,
      },
    );

    throw new Error(
      `Text prompt detection returned invalid response format: ${parseResult.error.issues
        .map((issue: any) => issue.message)
        .join(', ')}`,
    );
  }

  const validatedResponse = parseResult.data;

  if (validatedResponse.error) {
    log.error(
      'getTextPromptBlobs',
      `Text prompt detection result validation failed: ${response}. Error: ${validatedResponse.error.message}`,
      { errorCode: validatedResponse.error.code },
    );

    throw new Error(
      `Text prompt detection result validation failed: ${validatedResponse.error.message}`,
    );
  }

  if (validatedResponse.data) {
    const { blobs } = validatedResponse.data;

    log.info(
      'getTextPromptBlobs',
      `Text prompt detection completed successfully, found ${blobs.length} blobs`,
      {
        processingTimeMs: validatedResponse.data.processingTimeMs,
      },
    );

    // Adjust the detection box center and segment contour back to original
    // image space (both are in cropped-ROI coords from the GPU detector).
    return blobs.map((match) => shiftMatchByOffset(match, offsetX, offsetY));
  }

  throw new Error('Unexpected response type after validation');
}

// Serialize the step-config head (zod schema, see LocateTypes) into the proto
// oneof shape the ComputeDinoEmbeddings RPC expects. The zod `kind`/`version`
// fields are schema-level discriminators with no proto counterpart.
export function toLocateHeadProto(head: DinoFastLocateHead): LocateHeadProto {
  if (head.kind === 'mlp') {
    return {
      mlp: {
        inputDim: head.inputDim,
        hidden: head.hidden,
        w1: head.w1,
        b1: head.b1,
        w2: head.w2,
        b2: head.b2,
      },
    };
  }

  return {
    logistic: { inputDim: head.inputDim, w: head.w, b: head.b },
  };
}

export async function callComputeDinoEmbeddings({
  image,
  modelVersion,
  precision,
  locateHead,
  omitTensor = false,
  equipment,
  vision,
  gpuTaskRunnerId,
}: {
  image: ArrayBuffer;
  modelVersion: string;
  precision: string;
  // When set, vision-bot forward-scores the patch grid with this head and
  // returns `heatmapFp16`; pair it with `omitTensor: true` to skip the ~1MB
  // tensor payload. Plain embedding calls leave both unset and keep the
  // patch tensor.
  locateHead?: LocateHeadProto;
  omitTensor?: boolean;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
}): Promise<ComputeDinoEmbeddingsResponse> {
  const request = {
    image: new Uint8Array(image),
    modelVersion,
    precision,
    locateHead,
    omitTensor,
  };

  if (!gpuTaskRunnerId) {
    return vision.computeDinoEmbeddings(request);
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'callComputeDinoEmbeddings',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.computeDinoEmbeddings(request);
  }

  return gpuTaskRunner.computeDinoEmbeddings(request);
}

export async function callComputeDinoImageEmbedding({
  image,
  modelVersion,
  precision,
  equipment,
  vision,
  gpuTaskRunnerId,
}: {
  image: ArrayBuffer;
  modelVersion: string;
  precision: string;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
}): Promise<ComputeDinoImageEmbeddingResponse> {
  const request = {
    image: new Uint8Array(image),
    modelVersion,
    precision,
  };

  if (!gpuTaskRunnerId) {
    return vision.computeDinoImageEmbedding(request);
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'callComputeDinoImageEmbedding',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.computeDinoImageEmbedding(request);
  }

  return gpuTaskRunner.computeDinoImageEmbedding(request);
}

// Mirrors callComputeDinoEmbeddings: routes through the GPU Task Runner when
// configured + healthy, falls back to the local vision service otherwise.
export async function callTrainLocateHead({
  image,
  sessionId,
  seq,
  clicks,
  committedPositives,
  committedNegatives,
  headKind,
  modelVersion,
  precision,
  equipment,
  vision,
  gpuTaskRunnerId,
}: {
  image: ArrayBuffer;
  sessionId: string;
  seq: number;
  clicks: Array<{ nx: number; ny: number; positive: boolean }>;
  committedPositives: Array<{ values: number[] }>;
  committedNegatives: Array<{ values: number[] }>;
  headKind: 'mlp' | 'logistic';
  modelVersion: string;
  precision: string;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
}): Promise<TrainLocateHeadResponse> {
  const request = {
    sessionId,
    seq,
    image: new Uint8Array(image),
    modelVersion,
    precision,
    clicks,
    committedPositives,
    committedNegatives,
    headKind:
      headKind === 'mlp'
        ? LocateHeadKind.LOCATE_HEAD_KIND_MLP
        : LocateHeadKind.LOCATE_HEAD_KIND_LOGISTIC,
  };

  if (!gpuTaskRunnerId) {
    return vision.trainLocateHead(request);
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'callTrainLocateHead',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.trainLocateHead(request);
  }

  return gpuTaskRunner.trainLocateHead(request);
}

// Mirrors callComputeDinoEmbeddings: routes through the GPU Task Runner when
// configured + healthy, falls back to the local vision service otherwise.
export async function callSegmentMask({
  image,
  boxes,
  method,
  heatmapSamples,
  heatmapThreshold,
  heatmapDense,
  equipment,
  vision,
  gpuTaskRunnerId,
}: {
  image: ArrayBuffer;
  // `points` is optional: the DinoFast live-detection path supplies heatmap
  // point prompts, while the teach-modal path sends box-only prompts.
  boxes: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    points?: { x: number; y: number; positive: boolean }[];
  }[];
  // Segmentation backend: 'sam2' (default), 'guided_filter', or
  // 'bilateral_solver'. Defaults to 'sam2' so existing callers compile
  // and behave unchanged without passing this arg.
  method?: 'sam2' | 'guided_filter' | 'bilateral_solver' | 'heatmap_threshold';
  // Optional dense heatmap samples (cropped-ROI pixel space) + the object/
  // background threshold. When supplied, vision-bot runs SAM2 multimask and
  // picks the heatmap-aligned candidate per box; omitted ⇒ single-mask.
  heatmapSamples?: { x: number; y: number; score: number }[];
  heatmapThreshold?: number;
  // Dense fp16 heatmap. The edge-aware filter methods (guided_filter /
  // bilateral_solver) consume it as their input; SAM2 uses it (when present)
  // to gate masks back to the heatmap's foreground support.
  heatmapDense?: { data: Uint8Array; width: number; height: number };
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
}): Promise<SegmentMaskResponse> {
  // Normalize to the generated request shape (Box2DXyxy.points is required);
  // a missing `points` becomes an empty list = box-only prompt.
  const methodEnum = {
    sam2: 0,
    guided_filter: 1,
    bilateral_solver: 2,
    heatmap_threshold: 3,
  }[method ?? 'sam2'];

  const request = {
    image: new Uint8Array(image),
    boxes: boxes.map((b) => ({ ...b, points: b.points ?? [] })),
    method: methodEnum,
    heatmapSamples: heatmapSamples ?? [],
    heatmapThreshold: heatmapThreshold ?? 0,
    heatmapDense: heatmapDense?.data ?? new Uint8Array(0),
    heatmapWidth: heatmapDense?.width ?? 0,
    heatmapHeight: heatmapDense?.height ?? 0,
  };

  if (!gpuTaskRunnerId) {
    return vision.segmentMask(request);
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'callSegmentMask',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.segmentMask(request);
  }

  return gpuTaskRunner.segmentMask(request);
}

// Teach-time: establish the canonical orientation reference from a taught
// contour. Forwards the contour to vision-bot's ComputeOrientationReference
// RPC and maps the response oneof back to a plain { isReliable, reference
// contour } shape. On the error branch (or an unreliable shape) the
// referenceContour is empty and the frontend persists nothing.
// vision-bot's enum ordinals, mapped to the string form the wizard persists and
// the UI switches on. UNSPECIFIED (and anything unrecognized) becomes null —
// callers treat that as "no reference, reason unknown".
const ORIENTATION_REJECTION_BY_ORDINAL: Record<
  number,
  OrientationReferenceRejection
> = {
  1: 'symmetric',
  2: 'sceneInconsistent',
};

export async function callComputeOrientationReference({
  contour,
  siblingContours,
  vision,
}: {
  contour: { x: number; y: number }[];
  // The teach scene's other matches. vision-bot registers each against the
  // primary and withholds the reference unless they all agree.
  siblingContours?: { x: number; y: number }[][];
  vision: VisionInterface;
}): Promise<{
  isReliable: boolean;
  referenceContour: number[][];
  rejection: OrientationReferenceRejection | null;
}> {
  const response = await vision.computeOrientationReference({
    contour: contour.map((p) => ({ x: p.x, y: p.y })),
    siblingContours: (siblingContours ?? []).map((sibling) => ({
      points: sibling.map((p) => ({ x: p.x, y: p.y })),
    })),
  });

  if (response.error) {
    throw new Error(
      `ComputeOrientationReference failed: ${response.error.message}`,
    );
  }

  const { data } = response;

  if (!data || !data.isReliable || !data.reference) {
    return {
      isReliable: false,
      referenceContour: [],
      rejection: ORIENTATION_REJECTION_BY_ORDINAL[data?.rejection ?? 0] ?? null,
    };
  }

  return {
    isReliable: true,
    referenceContour: data.reference.referenceContour.map((p) => [p.x, p.y]),
    rejection: null,
  };
}

// Convert an oriented detection box (center + width/height + rotation) to the
// tightest axis-aligned xyxy bbox that contains its rotated corners. SAM2's
// box prompts are axis-aligned only — feeding it the rotated rect directly
// would either crash or be silently misinterpreted as xyxy. The box is the
// pre-segmentation detector result (Detection2D).
function obbToAxisAlignedXyxy(detection: Detection2D): {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
} {
  const cos = Math.cos(detection.rotation);
  const sin = Math.sin(detection.rotation);
  const halfW = detection.width / 2;
  const halfH = detection.height / 2;

  const corners = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ].map(([dx, dy]) => ({
    x: detection.x + dx * cos - dy * sin,
    y: detection.y + dx * sin + dy * cos,
  }));

  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);

  return {
    x1: Math.min(...xs),
    y1: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys),
  };
}

// Parse the source dimensions out of a JPEG's Start-Of-Frame marker.
// Returns `undefined` if the input isn't a recognizable baseline JPEG — the
// caller falls back to the encoder's letterbox content dims in that case.
//
// JPEG layout: starts with SOI (0xFFD8), then a sequence of segments each
// beginning with 0xFF<marker>. SOFn markers (0xC0..0xCF, except 0xC4/C8/CC)
// carry: length(2), precision(1), height(2), width(2). We don't need to
// fully decode — just walk segments until we hit any SOFn.
function getJpegSize(
  image: ArrayBuffer,
): { width: number; height: number } | undefined {
  if (image.byteLength < 4) return undefined;

  const view = new DataView(image);

  if (view.getUint16(0) !== 0xffd8) return undefined;

  let i = 2;

  while (i + 1 < view.byteLength) {
    if (view.getUint8(i) !== 0xff) return undefined;

    let marker = view.getUint8(i + 1);

    // 0xFF padding bytes between markers.
    while (marker === 0xff && i + 2 < view.byteLength) {
      i += 1;
      marker = view.getUint8(i + 1);
    }

    i += 2;

    // SOFn (baseline / progressive / lossless / etc.) — carry width+height.
    if (
      marker >= 0xc0 &&
      marker <= 0xcf &&
      marker !== 0xc4 && // DHT
      marker !== 0xc8 && // JPG (reserved)
      marker !== 0xcc // DAC
    ) {
      if (i + 7 > view.byteLength) return undefined;

      return {
        height: view.getUint16(i + 3),
        width: view.getUint16(i + 5),
      };
    }

    // Standalone markers with no length payload: SOI/EOI/RSTn.
    if (
      marker === 0xd8 ||
      marker === 0xd9 ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      continue; // eslint-disable-line no-continue
    }

    if (i + 2 > view.byteLength) return undefined;

    const segmentLength = view.getUint16(i);

    if (segmentLength < 2) return undefined;

    i += segmentLength;
  }

  return undefined;
}

export interface DinoFastDetections {
  matches: LocateMatch[];
  // The dense score map the matches were thresholded from; populated only
  // when `returnHeatmap` was requested (teach-preview overlay), null
  // otherwise.
  heatmap: DinoFastDenseHeatmap | null;
}

export async function getDinoFastBlobs({
  image,
  regionOfInterest,
  params,
  equipment,
  vision,
  returnHeatmap = false,
}: {
  image: ArrayBuffer;
  regionOfInterest?: RegionOfInterest;
  params: DinoFastParams;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  returnHeatmap?: boolean;
}): Promise<DinoFastDetections> {
  // Apply the user's ROI before encoding so embeddings cover the cropped
  // region only — matches getTextPromptBlobs / getCustomCheckpointBlobs.
  // `vision.preprocessImage` handles the "ROI = full image" case as a no-op.
  let croppedImage = image;
  let offsetX = 0;
  let offsetY = 0;

  if (regionOfInterest) {
    const preprocessed = await vision.preprocessImage(image, regionOfInterest);
    croppedImage = preprocessed.croppedImage;
    offsetX = preprocessed.offsetX;
    offsetY = preprocessed.offsetY;
  }

  // Read the source-image dims directly from the JPEG header so we can map
  // patch coords back to the actual frame's pixel space. The encoder rescales
  // the source down to its square `inputW × inputH` letterbox, so deriving
  // dims from `inputW - 2 * paddingLeft` would silently return them in the
  // *scaled* space — bboxes would then render in the wrong area of the live
  // video stream (which has its own native resolution).
  const sourceSize = getJpegSize(croppedImage);

  // A learned head is forward-scored by vision-bot itself (the response
  // carries the fp16 heatmap), so the raw patch tensor is only downloaded for
  // the client-side cosine scorer.
  const tensorResp = await callComputeDinoEmbeddings({
    image: croppedImage,
    modelVersion: params.embeddingModelVersion,
    precision: params.precision,
    locateHead: params.locateHead
      ? toLocateHeadProto(params.locateHead)
      : undefined,
    omitTensor: params.locateHead != null,
    equipment,
    vision,
    gpuTaskRunnerId: params.gpuTaskRunnerId,
  });

  if (tensorResp.error) {
    log.error('runLocate.dinoFast', 'encoder failed', {
      detail: tensorResp.error.message,
    });

    throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_FAILED);
  }

  if (!tensorResp.data) {
    log.error('runLocate.dinoFast', 'encoder returned no data');
    throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_FAILED);
  }

  const { data } = tensorResp;

  // A placeholder `embeddingModelVersion` (empty default or legacy seed) was
  // never pinned to a real encoder capture, so it can't signal a genuine
  // mismatch — the encoder is authoritative and the references being matched
  // were embedded by whatever encoder is live now. Only a version that was
  // actually adopted from a prior encoder response indicates stale embeddings.
  if (
    !isUnpinnedModelVersion(params.embeddingModelVersion) &&
    data.modelVersion !== params.embeddingModelVersion
  ) {
    log.error('runLocate.dinoFast', 'encoder model mismatch', {
      saved: params.embeddingModelVersion,
      running: data.modelVersion,
    });

    throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_MISMATCH);
  }

  // Learned head (MLP or logistic) replaces cosine scoring when present
  // (trained in the teach UI and serialized into the step config): vision-bot
  // forward-scores the grid server-side and the response omits the raw tensor
  // entirely, so it must not be reconstructed here — only the fp16 heatmap is
  // decoded. Legacy/untaught steps have no head and keep the original
  // client-side cosine scorer unchanged.
  let heatmap: Float32Array;

  if (params.locateHead) {
    if (!data.heatmapFp16) {
      log.error(
        'runLocate.dinoFast',
        'encoder returned no heatmap for a learned-head request',
      );

      throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_FAILED);
    }

    const fp16 = uint8BytesToUint16(data.heatmapFp16);

    if (fp16.length !== data.gridH * data.gridW) {
      log.error('runLocate.dinoFast', 'heatmap size mismatch', {
        expected: data.gridH * data.gridW,
        got: fp16.length,
        gridH: data.gridH,
        gridW: data.gridW,
      });

      throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_FAILED);
    }

    heatmap = new Float32Array(fp16.length);

    for (let i = 0; i < fp16.length; i += 1) {
      heatmap[i] = fp16ToFp32(fp16[i]);
    }
  } else {
    // The cosine scorer needs positive references; a learned head carries its
    // own weights and scores from the embeddings alone, so it doesn't.
    if (params.positives.length === 0) {
      log.error('runLocate.dinoFast', 'no positive references configured');
      throw new Error(CLICKFIND_ERROR_MESSAGES.NO_REFERENCES);
    }

    const tensor = PatchTensor.fromTensorBytes(
      data.gridH,
      data.gridW,
      data.embeddingDim,
      data.values,
    );

    heatmap = cosineSimilarityHeatmap({
      tensor,
      positives: params.positives,
      negatives: params.negatives,
      negativeWeight: DEFAULT_NEGATIVE_WEIGHT,
    });
  }

  // DinoFast always fits oriented bboxes to each cluster's principal axes
  // (PCA over patch coords). The old `disableRotation` axis-aligned mode was
  // removed — the SAM2 contour is the source of truth for the final shape, and
  // the detector box only needs to bound the cluster well enough to prompt it.
  const fitOriented = true;

  // Original-image dims: prefer the JPEG header (reflects the actual source
  // resolution, e.g. 1280×720) so bboxes land on the right part of the live
  // video preview. Fall back to the letterbox content dims if the header
  // couldn't be parsed (e.g. non-JPEG or in unit tests where the buffer is
  // synthetic) — those tests use a square `inputW = inputH` fixture, so the
  // fallback agrees with the source aspect there.
  const originalW = sourceSize?.width ?? data.inputW - 2 * data.paddingLeft;
  const originalH = sourceSize?.height ?? data.inputH - 2 * data.paddingTop;

  // Teach-preview overlay: ship the score map the detections below are
  // thresholded from. The learned-head path passes vision-bot's fp16 bytes
  // through untouched; the cosine path packs its locally computed fp32
  // scores. `crop` places the scored (ROI-cropped) region within the source
  // frame — offsetX/Y are 0 and the dims equal the frame when no crop
  // applied.
  const denseHeatmap: DinoFastDenseHeatmap | null = returnHeatmap
    ? {
        valuesFp16Base64: bytesToBase64(
          params.locateHead && data.heatmapFp16
            ? data.heatmapFp16
            : fp32ArrayToFp16Bytes(heatmap),
        ),
        gridH: data.gridH,
        gridW: data.gridW,
        inputW: data.inputW,
        inputH: data.inputH,
        paddingTop: data.paddingTop,
        paddingLeft: data.paddingLeft,
        modelVersion: data.modelVersion,
        crop: {
          x: offsetX,
          y: offsetY,
          width: originalW,
          height: originalH,
        },
      }
    : null;

  const rawBlobs = thresholdAndConnectedComponents({
    heatmap,
    gridH: data.gridH,
    gridW: data.gridW,
    threshold: params.scoreThreshold,
    // PCA over patch coords needs the indices preserved through CC.
    collectPatches: fitOriented,
  });

  const nmsBlobs = nonMaxSuppression(rawBlobs, params.nmsIouThreshold);

  const unletterboxCtx = {
    gridH: data.gridH,
    gridW: data.gridW,
    inputH: data.inputH,
    inputW: data.inputW,
    paddingTop: data.paddingTop,
    paddingLeft: data.paddingLeft,
    originalW,
    originalH,
  };

  // Match-area enforcement happens in pixel space on the final shape — the
  // contour when segmentation produced one, else the detector OBB box. (Patch
  // count was a coarse proxy for thresholded heatmap support, not true area.)
  const maxAreaBounded = !isUnboundedMaxMatchArea(params.maxMatchAreaPx2);

  const areaWithinBounds = (areaPx2: number): boolean =>
    areaPx2 >= params.minMatchAreaPx2 &&
    (!maxAreaBounded || areaPx2 <= params.maxMatchAreaPx2);

  // Segmentation off ⇒ the OBB box is the final shape, so filter detections by
  // box area before the maxBlobs cap (the cap then counts only valid matches).
  // Segmentation on ⇒ the box overestimates a small object's true extent, so we
  // do NOT pre-gate here; the contour filter at finalization is authoritative.
  const segmentationOff = !params.enableSegmentationMask;

  const detectionBoxWithinBounds = (b: {
    width: number;
    height: number;
  }): boolean => !segmentationOff || areaWithinBounds(b.width * b.height);

  // Final-match area filter: contour area when segmented, else box area. Used
  // on the segmentation-on return paths (the segmentation-off path is already
  // box-filtered pre-cap above).
  const matchWithinBounds = (m: {
    detection: Detection2D;
    segment: Blob2D | null;
  }): boolean =>
    areaWithinBounds(
      m.segment
        ? contourArea(m.segment.contour)
        : m.detection.width * m.detection.height,
    );

  // The heatmap-fit OBBs are the detector boxes (Detection2D). When
  // segmentation is enabled they prompt SAM2; the resulting contour becomes the
  // match's segment. When it is off, each detection becomes a segment-less
  // LocateMatch.
  let detections: Detection2D[];

  if (fitOriented) {
    const oriented = fitOrientedBlobs({
      blobs: nmsBlobs,
      gridW: data.gridW,
    });

    detections = oriented
      .map((b) => unletterboxOrientedBlob(b, unletterboxCtx))
      .filter(detectionBoxWithinBounds)
      .sort((a, b) => b.score - a.score)
      .slice(0, params.maxBlobs)
      .map((b) => ({
        // unletterboxOrientedBlob already returns center coords (box-center
        // convention); add the ROI offset.
        x: b.cx + offsetX,
        y: b.cy + offsetY,
        width: b.width,
        height: b.height,
        score: b.score,
        rotation: b.rotation,
      }));
  } else {
    detections = nmsBlobs
      .map((b) => unletterboxBlob(b, unletterboxCtx))
      .filter(detectionBoxWithinBounds)
      .sort((a, b) => b.score - a.score)
      .slice(0, params.maxBlobs)
      .map((b) => ({
        // unletterboxBlob returns top-left coords; the Detection2D x/y
        // convention is the bbox *center* (consumers like makeRectMaskRle and
        // the Blobs2DOverlay SVG both compute `x - width/2` to get the
        // top-left). Without this offset DinoFast detections render half-a-bbox
        // up-and-left of where the encoder actually saw the cluster, and
        // downstream pose estimation projects from the wrong pixel.
        x: b.x + offsetX + b.width / 2,
        y: b.y + offsetY + b.height / 2,
        width: b.width,
        height: b.height,
        score: b.score,
        rotation: 0,
      }));
  }

  // When segmentation is enabled, refine each DinoFast OBB with a SAM2 pass.
  // (Disabled ⇒ return the heatmap-patch bboxes as-is.) This is gated on the
  // explicit `enableSegmentationMask` toggle, independent of `precision` (which
  // only sets the DINO encoder grid resolution). vision-bot runs a lightweight
  // SAM2 variant — tiny on CPU, base-plus on GPU — which returns a contour-only
  // segment per detection (the detector OBB stays the box); the RLE mask rides
  // on the segment when available. We preserve the DinoFast similarity score on
  // the segment (SAM2 doesn't produce one).
  //
  // Send the cropped image (not the full frame) so SAM2's ViT encoder runs
  // on the smaller ROI pixels instead of re-encoding the whole frame. Adjust
  // box coords to cropped-image space on the way in, and add the ROI offset
  // back to blob centers + contour points on the way out.
  // No segmentation (or nothing detected): seg-off detections were already
  // box-area filtered before the maxBlobs cap; nothing more to do here.
  if (!params.enableSegmentationMask || detections.length === 0) {
    return {
      matches: detections.map((detection) => ({ detection, segment: null })),
      heatmap: denseHeatmap,
    };
  }

  // Clamp SAM2 box prompts to the cropped image bounds. The OBB envelope
  // can extend past the ROI when a detection sits right on the edge;
  // SAM2's set_image runs on cropped-image pixels only, so negative or
  // past-edge coords either crash or produce partial masks.
  const croppedW = originalW;
  const croppedH = originalH;

  const boxes = detections.map((b) => {
    const { x1, y1, x2, y2 } = obbToAxisAlignedXyxy(b);

    // Cropped-image (ROI) space — the space SAM2 and the patch grid share.
    // unletterboxCtx.originalW/H == croppedW/H, so derived point coords land
    // in this same space and need no further offset.
    const cropBox = {
      x1: Math.max(0, x1 - offsetX),
      y1: Math.max(0, y1 - offsetY),
      x2: Math.min(croppedW, x2 - offsetX),
      y2: Math.min(croppedH, y2 - offsetY),
    };

    // Positive points on the matched object + negative points on holes /
    // in-box background, so SAM2 carves out interior negative space instead
    // of filling the box. Empty when the blob is too sparse (box-only).
    const points = derivePointPrompts({
      heatmap,
      gridH: data.gridH,
      gridW: data.gridW,
      threshold: params.scoreThreshold,
      box: cropBox,
      ctx: unletterboxCtx,
      marginFraction: SAM2_SHADOW_MARGIN_FRACTION,
    });

    return { ...cropBox, points };
  });

  // Dense heatmap samples over the box regions let vision-bot run SAM2 in
  // multimask mode and pick, per box, the candidate whose interior best
  // matches the DINO heatmap — rejecting the over-inclusive mask that grabs
  // a cast shadow. Same cropped-ROI pixel space as the boxes/points above.
  const heatmapSamples = deriveHeatmapSamples({
    heatmap,
    gridH: data.gridH,
    gridW: data.gridW,
    ctx: unletterboxCtx,
    boxes,
    marginFraction: SAM2_SHADOW_MARGIN_FRACTION,
  });

  const useFilter = params.segmentationMethod !== 'sam2';

  // Dense fp16 heatmap, sent for every method: the filter methods consume it
  // as their input, and SAM2 uses it to gate its masks back to the heatmap's
  // foreground support (excising the cast-shadow bleed its box prompt pulls in
  // when there's no real image edge to stop it).
  const heatmapDense = rasterizeHeatmapFp16({
    heatmap,
    gridH: data.gridH,
    gridW: data.gridW,
    ctx: unletterboxCtx,
  });

  let segResp: SegmentMaskResponse;

  try {
    segResp = await callSegmentMask({
      image: croppedImage,
      boxes,
      method: params.segmentationMethod,
      heatmapSamples: useFilter ? undefined : heatmapSamples,
      heatmapDense,
      heatmapThreshold: params.scoreThreshold,
      equipment,
      vision,
      gpuTaskRunnerId: params.gpuTaskRunnerId,
    });
  } catch (e) {
    log.warn(
      'getDinoFastBlobs.sam2',
      'SAM2 RPC failed; returning DinoFast OBBs unrefined',
      { error: e instanceof Error ? e.message : String(e) },
    );

    return {
      matches: detections
        .map((detection) => ({ detection, segment: null }))
        .filter(matchWithinBounds),
      heatmap: denseHeatmap,
    };
  }

  if (segResp.error || !segResp.data) {
    log.warn(
      'getDinoFastBlobs.sam2',
      'SAM2 segmentation returned an error; returning DinoFast OBBs unrefined',
      { error: segResp.error },
    );

    return {
      matches: detections
        .map((detection) => ({ detection, segment: null }))
        .filter(matchWithinBounds),
      heatmap: denseHeatmap,
    };
  }

  const { blobs: segBlobs, wasSegmented } = segResp.data;

  // Pair each detector box with its SAM2 segment. The detection box stays the
  // DinoFast OBB (the source-of-truth box); the segment carries the SAM2
  // contour (source of truth for the mask). Boxes whose SAM2 pass produced no
  // mask keep `segment: null`.
  const matches = detections.map((detection, i) => {
    const segBlob = segBlobs[i];

    if (!segBlob || !wasSegmented[i]) {
      return { detection, segment: null };
    }

    const sourceFrameContour = segBlob.contour.map((p) => ({
      x: p.x + offsetX,
      y: p.y + offsetY,
    }));

    const segment: Blob2D = {
      // Preserve the DinoFast cosine-similarity score — SAM2 emits 0.
      score: detection.score,
      contour: sourceFrameContour,
      // Intentionally drop SAM2's maskRle/maskWidth/maskHeight: they're in
      // cropped-image pixel space (SAM2 ran on the ROI crop), but downstream
      // consumers — e.g. 3D pose estimation via `makeRectMaskRle` — expect
      // the mask to be aligned with the source frame and sized to the full
      // image. Re-encoding the mask into source-frame coords just to satisfy
      // those consumers would be costly; the rectangular bbox fallback at
      // the pose-estimation step rebuilds a correctly framed mask from the
      // detection box instead.
    };

    return { detection, segment };
  });

  return { matches: matches.filter(matchWithinBounds), heatmap: denseHeatmap };
}

async function get2DBlobs({
  camera,
  method,
  regionOfInterest,
  vision,
  equipment,
  returnHeatmap = false,
}: RunLocateArgs): Promise<{
  blobs: LocateMatch[];
  image: ArrayBuffer;
  // Set only for the DinoFast method when `returnHeatmap` was requested
  // (preview overlay); null/undefined for every other method or when not asked.
  heatmap?: DinoFastDenseHeatmap | null;
}> {
  if (!camera.equipment) {
    throw new Error('Camera equipment is required for get2DBlobs');
  }

  const image = await getCameraColorFrame(camera.equipment, camera.integration);

  let blobs: LocateMatch[];
  let heatmap: DinoFastDenseHeatmap | null = null;

  switch (method.kind) {
    case 'BlobDetection2D': {
      const { disableRotation, ...visionParams } = method.settings;

      blobs = await vision.detect2DBlobs(image, regionOfInterest, visionParams);

      if (disableRotation) {
        blobs = applyDisableRotation(blobs);
      }

      break;
    }
    case 'ShapeDetection2D': {
      const { disableRotation, ...visionParams } = method.settings;

      blobs = await vision.detect2DShapes(
        image,
        method.templateImage,
        regionOfInterest,
        visionParams,
      );

      if (disableRotation) {
        blobs = applyDisableRotation(blobs);
      }

      break;
    }
    case 'TextPromptDetection2D': {
      blobs = await getTextPromptBlobs({
        image,
        regionOfInterest,
        params: method.settings,
        equipment,
        vision,
      });

      break;
    }
    // This locates the center of a the chessboard used for accuracy calibration
    case 'ChessboardDetection2D': {
      const result = await vision.getChessboardCorners(image, {
        kind: 'chessboard',
        rows: 3,
        cols: 3,
        squareSize: 0.03426,
      });

      blobs = [
        {
          detection: {
            x: result.corners[4].x, // center corner
            y: result.corners[4].y, // center corner
            width: 1,
            height: 1,
            score: 0,
            rotation: 0,
          },
          segment: null,
        },
      ];

      break;
    }
    case 'CustomCheckpointDetection2D': {
      blobs = await getCustomCheckpointBlobs({
        image,
        regionOfInterest,
        method,
        equipment,
        vision,
      });

      break;
    }
    case 'ManualBoundingBox': {
      blobs = [
        {
          detection: {
            x: method.settings.cx,
            y: method.settings.cy,
            width: method.settings.width,
            height: method.settings.height,
            score: 1.0,
            rotation: 0,
          },
          segment: null,
        },
      ];

      break;
    }
    case 'DinoFast': {
      // The locate path normally skips the dense heatmap (a preview-only
      // payload), but the ClickFind Locate step in the routine editor sets
      // `returnHeatmap` so it can render the same heatmap overlay as the Teach
      // page. Routine execution leaves it off.
      ({ matches: blobs, heatmap } = await getDinoFastBlobs({
        image,
        regionOfInterest,
        params: method.settings,
        equipment,
        vision,
        returnHeatmap,
      }));

      break;
    }
    default:
      throw new Error(`Unsupported method ${(method as LocateMethod).kind}`);
  }

  return { blobs, image, heatmap };
}

async function getIntrinsics(
  intrinsics: CameraIntrinsics | CameraIntrinsicsExtended | undefined,
  equipment: Camera | WristCamera,
): Promise<CameraIntrinsics | CameraIntrinsicsExtended> {
  let out = intrinsics;

  if (!out) {
    const config = equipment.getConfig() as
      | CameraConfiguration
      | WristCameraConfiguration;

    try {
      out = await equipment.getIntrinsics();
    } catch {
      // Calibrated intrinsics not available — fall back to built-in (e.g. camera-bot-cpp RPC)
      log.warn(
        'getIntrinsics',
        'No calibrated intrinsics, trying built-in intrinsics',
      );

      out = await equipment.getBuiltInIntrinsics();
    }

    if (out == null) {
      log.error('getIntrinsics', 'No intrinsics found for camera', {
        config,
      });

      throw new Error(`No intrinsics found for camera ${config.name}`);
    }

    log.info('getIntrinsics', 'Using intrinsics from camera', {
      intrinsics: out,
      config,
    });
  } else {
    log.info('getIntrinsics', 'Using intrinsics from args', {
      intrinsics: out,
    });
  }

  return out;
}

function buildPlaneMethod(
  mode: PlaneDetectionModeType,
  plane: Plane | undefined,
  objectHeight: number,
  ransacParams?: RunLocateArgs['ransacParams'],
  maxTiltAngle?: number,
): RunPlaneProjectionRequest['planeMethod'] {
  switch (mode) {
    case 'Manual': {
      if (!plane) {
        throw new Error(
          'Plane is required for Manual plane detection mode. Ensure a workplane is configured.',
        );
      }

      return {
        manual: {
          origin: {
            x: plane.origin.x,
            y: plane.origin.y,
            z: plane.origin.z,
          },
          plusX: { x: plane.plusX.x, y: plane.plusX.y, z: plane.plusX.z },
          plusY: { x: plane.plusY.x, y: plane.plusY.y, z: plane.plusY.z },
          objectHeight,
        },
      };
    }

    case 'Geometric':
      return {
        geometric: {
          maxIterations:
            ransacParams?.maxIterations ?? DEFAULT_RANSAC_MAX_ITERATIONS,
          distanceThreshold:
            ransacParams?.distanceThreshold ??
            DEFAULT_RANSAC_DISTANCE_THRESHOLD,
          minSamples: ransacParams?.minSamples ?? DEFAULT_RANSAC_MIN_SAMPLES,
          maxTiltAngle,
        },
      };

    case 'Learned':
      return { learned: { modelPath: '' } };

    default:
      throw new Error(`Unknown plane detection mode: ${mode}`);
  }
}

function buildPlaneProjectionRequest(
  blobs: LocateMatch[],
  image: ArrayBuffer,
  intrinsics: CameraIntrinsics | CameraIntrinsicsExtended,
  extrinsics: ExtrinsicsKinematics,
  planeMethod: RunPlaneProjectionRequest['planeMethod'],
  getState: () => RoutineRunnerState,
  graspFrame: RunLocateArgs['graspFrame'],
  orientationReference: RunLocateArgs['orientationReference'],
  depthImage?: ArrayBuffer,
  includeDebugVisualization?: boolean,
): RunPlaneProjectionRequest {
  const extended = intrinsics as CameraIntrinsicsExtended;

  return {
    // Map zod LocateMatch (segment: null) → proto LocateMatch (segment: undefined)
    detections: blobs.map((m) => ({
      ...m,
      segment: m.segment ?? undefined,
    })),
    image: new Uint8Array(image),
    depthImage: depthImage ? new Uint8Array(depthImage) : undefined,
    cameraParams: {
      intrinsics: {
        width: intrinsics.width,
        height: intrinsics.height,
        fx: intrinsics.fx,
        fy: intrinsics.fy,
        ppx: intrinsics.ppx,
        ppy: intrinsics.ppy,
        distortionCoefficients: extended.distortionCoefficients ?? [],
        distortionModel: extended.distortionModel ?? undefined,
      },
      extrinsics: extrinsics.extrinsics,
      extrinsicsKind:
        extrinsics.kind === 'tcp' ? ExtrinsicsKind.TCP : ExtrinsicsKind.BASE,
      robotPose:
        extrinsics.kind === 'tcp'
          ? getState().kinematicState.wristPose
          : undefined,
    },
    planeMethod,
    graspFrame: GRASP_FRAME_MAP[graspFrame ?? 'object'],
    // Map the persisted [x, y] pairs back to Point2D[] for the wire. Left
    // unset when no reliable reference was taught (null) ⇒ vision-bot falls
    // back to plane orientation.
    orientationReference: orientationReference
      ? {
          referenceContour: orientationReference.referenceContour.map(
            ([x, y]) => ({ x, y }),
          ),
        }
      : undefined,
    includeDebugVisualization,
  };
}

// Map a vision-bot proto Blob2D (Detection3D.segment) back to the zod Blob2D
// shape used by LocateResult/LocateMatch. Returns null when the proto segment
// is absent, so the box-only overlay path is preserved. meanIntensity and
// circularity are detect-time only and not carried on the wire, so they're
// omitted here.
function protoSegmentToBlob2D(segment: ProtoBlob2D | undefined): Blob2D | null {
  if (!segment) return null;

  return {
    score: segment.score,
    contour: segment.contour.map((p) => ({ x: p.x, y: p.y })),
    area: segment.area ?? undefined,
    classId: segment.classId ?? undefined,
    className: segment.className ?? undefined,
    // Cast to the zod-inferred field type rather than `Uint8Array<ArrayBuffer>`:
    // the proto bytes are `Uint8Array<ArrayBufferLike>` while Blob2D infers
    // `Uint8Array<ArrayBuffer>` under TS >=5.7, so the bare assignment trips
    // TS2322 in the api-srv Cloud Run deploy build (which installs a newer TS).
    // Targeting `Blob2D['maskRle']` stays valid under both that TS and the
    // local/pinned TS 5.1.3 where `Uint8Array` is non-generic.
    maskRle: (segment.maskRle ?? undefined) as Blob2D['maskRle'],
    maskWidth: segment.maskWidth ?? undefined,
    maskHeight: segment.maskHeight ?? undefined,
  };
}

// Maps the runLocate `graspFrame` arg to the vision-bot proto enum. Defaults to
// OBJECT (the detection principal axis) when the frame is omitted.
const GRASP_FRAME_MAP: Record<
  NonNullable<RunLocateArgs['graspFrame']>,
  GraspFrame
> = {
  object: GraspFrame.OBJECT,
  plane: GraspFrame.PLANE,
  world: GraspFrame.WORLD,
};

const POSE_METHOD_MAP: Record<string, PoseEstimationMethod> = {
  OBB_DEPTH: PoseEstimationMethod.OBB_DEPTH,
  OBB_ICP: PoseEstimationMethod.OBB_ICP,
  FPFH_ICP: PoseEstimationMethod.FPFH_ICP,
};

const POSE_METHODS_USING_REFERENCE = new Set(['OBB_ICP', 'FPFH_ICP']);

/**
 * Create RLE-encoded bytes for a rectangular mask from a bounding box.
 * Used as a SAM2-free fallback: generates a filled-rectangle mask so that
 * Run3DPoseEstimation can segment depth within the detection bbox.
 *
 * Format: 1 byte (start value 0 or 1), then 4-byte LE uint32 run lengths,
 * alternating between the start value and its complement.
 */
function makeRectMaskRle(
  cx: number,
  cy: number,
  w: number,
  h: number,
  imgW: number,
  imgH: number,
): Uint8Array {
  const x1 = Math.max(0, Math.floor(cx - w / 2));
  const y1 = Math.max(0, Math.floor(cy - h / 2));
  const x2 = Math.min(imgW, Math.floor(cx + w / 2));
  const y2 = Math.min(imgH, Math.floor(cy + h / 2));

  if (x2 <= x1 || y2 <= y1) {
    return new Uint8Array(0);
  }

  const rectW = x2 - x1;
  const numRows = y2 - y1;
  const rowGap = imgW - rectW;

  const leadingZeros = y1 * imgW + x1;
  const trailingZeros = imgW - x2 + (imgH - y2) * imgW;

  const runLengths: number[] = [];
  const startValue = leadingZeros > 0 ? 0 : 1;

  if (leadingZeros > 0) {
    runLengths.push(leadingZeros);
  }

  if (rowGap === 0) {
    // Rectangle spans full width — ones are contiguous
    runLengths.push(rectW * numRows);
  } else {
    for (let row = 0; row < numRows; row += 1) {
      runLengths.push(rectW);

      if (row < numRows - 1) {
        runLengths.push(rowGap);
      }
    }
  }

  if (trailingZeros > 0) {
    runLengths.push(trailingZeros);
  }

  const buffer = new ArrayBuffer(1 + 4 * runLengths.length);
  const view = new DataView(buffer);
  view.setUint8(0, startValue);

  let offset = 1;

  for (const len of runLengths) {
    view.setUint32(offset, len, true);
    offset += 4;
  }

  return new Uint8Array(buffer);
}

export interface Estimate3DPosesArgs {
  /**
   * 2D matches (detector box + optional mask segment) feeding the pose
   * estimator. This is the proto `LocateMatch` shape (assigned directly to
   * `Run3DPoseEstimationRequest.detections`), not the zod types from
   * LocateTypes — the two differ in `maskRle`'s typed array generic
   * (`Uint8Array<ArrayBufferLike>` vs `Uint8Array<ArrayBuffer>`).
   */
  blobs: Run3DPoseEstimationRequest['detections'];
  /** Raw depth frame. Copied to a Uint8Array before the RPC. */
  depthImage: ArrayBuffer | Uint8Array;
  /** Camera intrinsics/extrinsics + robot pose for unprojection. */
  cameraParams: CameraParams;
  /** Shared 3D pose-estimation tuning (poseMethod, cadSource, thresholds…). */
  config: Detection3DPoseEstimationConfig;
  /** Vision client used to call run3DPoseEstimation. */
  vision: VisionInterface;
  /** When true, vision-bot renders a debug overlay into the response. */
  includeDebugVisualization?: boolean;
}

export interface Estimate3DPosesResult {
  detections: Detection3D[];
  debugVisualization?: Uint8Array;
}

/**
 * Stage 2 of the 3D locate pipeline: given already-computed 2D detections
 * (with masks), a depth frame, and camera parameters, build a
 * `Run3DPoseEstimationRequest` and call `vision.run3DPoseEstimation` to get
 * 6D poses back.
 *
 * Extracted verbatim from `run3DLocate` so callers that produce their own 2D
 * blobs (e.g. the LocateSkill's 3D-detection projection) can reuse the exact
 * same pose-estimation request construction and response handling.
 *
 * The objectReference / CAD pose-options logic, tuning defaults, and response
 * parsing are byte-for-byte identical to the original inline block — only the
 * inputs are parameterized.
 */
export async function estimate3DPosesFromBlobs(
  args: Estimate3DPosesArgs,
): Promise<Estimate3DPosesResult> {
  const { blobs, depthImage, cameraParams, config, vision } = args;

  const poseRequest: Run3DPoseEstimationRequest = {
    detections: blobs,
    depthImage: new Uint8Array(depthImage),
    cameraParams,
    poseMethod:
      POSE_METHOD_MAP[config.poseMethod] ?? PoseEstimationMethod.OBB_DEPTH,
    objectReference:
      POSE_METHODS_USING_REFERENCE.has(config.poseMethod) &&
      config.cadSource === 'PRIMITIVE'
        ? {
            primitiveShape: {
              shapeType:
                config.primitiveShapeType === 'CYLINDER'
                  ? PrimitiveShapeType.CYLINDER
                  : PrimitiveShapeType.BOX,
              dimX: config.primitiveDimX,
              dimY: config.primitiveDimY,
              dimZ: config.primitiveDimZ,
            },
          }
        : undefined,
    confidenceThreshold: config.confidenceThreshold,
    maxDetections: config.maxDetections,
    depthOutlierPercentile: config.depthOutlierPercentile,
    // FPFH-specific tuning; ignored by OBB_DEPTH / OBB_ICP in vision-bot.
    ransacRestarts:
      config.poseMethod === 'FPFH_ICP' ? config.fpfhRansacRestarts : undefined,
    voxelSize:
      config.poseMethod === 'FPFH_ICP' ? config.fpfhVoxelSize : undefined,
    includeDebugVisualization: args.includeDebugVisualization,
  };

  log.debug('estimate3DPoses.request', 'Sending 3D pose estimation request', {
    detectionCount: poseRequest.detections.length,
    poseMethod: config.poseMethod,
    depthImageBytes: poseRequest.depthImage.length,
  });

  if (
    POSE_METHODS_USING_REFERENCE.has(config.poseMethod) &&
    config.cadSource === 'CAD_FILE'
  ) {
    if (!config.cadModelStorageID || !config.cadModelFormat) {
      throw new Error(
        'CAD_FILE reference is selected but no CAD model has been uploaded. ' +
          'Upload a .stl, .obj, or .ply file in the 3D Detection settings, ' +
          'or pick a different reference model.',
      );
    }
  }

  const poseOptions =
    POSE_METHODS_USING_REFERENCE.has(config.poseMethod) &&
    config.cadSource === 'CAD_FILE' &&
    config.cadModelStorageID &&
    config.cadModelFormat
      ? {
          cadModelStorageID: config.cadModelStorageID,
          cadModelFormat: config.cadModelFormat,
          cadModelUnits: config.cadModelUnits,
        }
      : undefined;

  const poseResponse = await vision.run3DPoseEstimation(
    poseRequest,
    poseOptions,
  );

  if (poseResponse.error) {
    throw new Error(`3D pose estimation failed: ${poseResponse.error.message}`);
  }

  if (!poseResponse.data) {
    throw new Error('3D pose estimation returned no data');
  }

  log.info('estimate3DPoses.result', '3D pose estimation completed', {
    detectionCount: poseResponse.data.detections.length,
    processingTimeMs: poseResponse.data.processingTimeMs,
  });

  return {
    detections: poseResponse.data.detections,
    debugVisualization: poseResponse.data.debugVisualization,
  };
}

/**
 * Convert detected 2D matches into proto LocateMatch detections suitable for 3D
 * pose estimation. Drops sub-5px detections (by detector box), preserves the
 * SAM2 mask segment when present, and falls back to a rectangular bbox mask
 * (sized to the full image, derived from the detector box) otherwise.
 *
 * Extracted verbatim from the CPU path of `run3DLocate` so the LocateSkill
 * 3D-detection projection can reuse identical conversion + mask-fallback logic.
 */
export function blobsToDetectionsWithMasks(
  matches: LocateMatch[],
  imgWidth: number,
  imgHeight: number,
): Run3DPoseEstimationRequest['detections'] {
  return matches
    .filter(({ detection }) => detection.width >= 5 && detection.height >= 5)
    .map(({ detection, segment }) => ({
      detection: {
        x: detection.x,
        y: detection.y,
        width: detection.width,
        height: detection.height,
        rotation: detection.rotation,
        score: detection.score,
        classId: detection.classId,
        className: detection.className,
      },
      // Segment carries the contour + mask. Use the SAM2 segment's mask when
      // present, else synthesize a rectangular mask from the detector box so
      // Run3DPoseEstimation can segment depth within the bbox.
      segment: {
        score: detection.score,
        contour: segment?.contour ?? [],
        area: segment?.area,
        classId: detection.classId,
        className: detection.className,
        maskRle:
          segment?.maskRle ??
          makeRectMaskRle(
            detection.x,
            detection.y,
            detection.width,
            detection.height,
            imgWidth,
            imgHeight,
          ),
        maskWidth: segment?.maskWidth ?? imgWidth,
        maskHeight: segment?.maskHeight ?? imgHeight,
      },
    }));
}

/**
 * Build the `CameraParams` (intrinsics, extrinsics, extrinsics kind, robot
 * pose) needed for 3D pose estimation. Extracted verbatim from "Step 3" of
 * `run3DLocate` so both the Detection3D method path and the 2D-method
 * 3D-detection projection path share identical camera-param construction.
 */
async function buildCameraParams(
  args: RunLocateArgs,
  preResolvedIntrinsics?: CameraIntrinsics | CameraIntrinsicsExtended,
): Promise<CameraParams> {
  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for buildCameraParams');
  }

  // Callers that already fetched intrinsics (e.g. for mask dims) pass them in
  // to avoid a redundant camera round-trip for the identical value.
  const intrinsics =
    preResolvedIntrinsics ??
    (await getIntrinsics(args.intrinsics, args.camera.equipment));

  const extrinsics = await resolveVisionExtrinsics(
    args.camera.equipment,
    args.extrinsics,
  );

  const extended = intrinsics as CameraIntrinsicsExtended;

  return {
    intrinsics: {
      width: intrinsics.width,
      height: intrinsics.height,
      fx: intrinsics.fx,
      fy: intrinsics.fy,
      ppx: intrinsics.ppx,
      ppy: intrinsics.ppy,
      distortionCoefficients: extended.distortionCoefficients ?? [],
      distortionModel: extended.distortionModel ?? undefined,
    },
    extrinsics: extrinsics.extrinsics,
    extrinsicsKind:
      extrinsics.kind === 'tcp' ? ExtrinsicsKind.TCP : ExtrinsicsKind.BASE,
    robotPose:
      extrinsics.kind === 'tcp'
        ? args.getState().kinematicState.wristPose
        : undefined,
  };
}

/**
 * Post-process raw Detection3D results into ordered, limited `LocateResult[]`.
 *
 * Extracted verbatim from "Step 4.5 + Step 5" of `run3DLocate`:
 *   - drop detections below `detection3dConfig.minInlierRatio`,
 *   - apply accuracy-calibration correction to each pose,
 *   - apply the user-defined transform relative to the detected pose,
 *   - convert to LocateResult (blob + pose + extents),
 *   - sort by `resultOrdering` and slice to `resultsLimit`.
 *
 * Shared by the Detection3D method path and the 2D-method 3D-detection
 * projection path; both pass the relevant `Detection3DPoseEstimationConfig`
 * (the Detection3D method's `settings` or the projection `detection3dConfig`).
 */
export function finalizeDetection3DResults(params: {
  rawDetections: Detection3D[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
  config: Detection3DPoseEstimationConfig;
  args: RunLocateArgs;
}): {
  results: LocateResult[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
} {
  const { rawDetections, image, debugVisualization, config, args } = params;

  // Step 4.5: Drop detections whose ICP/RANSAC fit falls below the
  // user-configured minimum. A misaligned pose is worse than no pose —
  // downstream motion and visualization see the filtered set only.
  const minInlierRatio = config.minInlierRatio ?? 0;

  const keptDetections =
    minInlierRatio > 0
      ? rawDetections.filter((detection) => {
          const fit = detection.inlierRatio;
          if (fit === undefined || fit === null) return true;

          return fit >= minInlierRatio;
        })
      : rawDetections;

  if (minInlierRatio > 0 && keptDetections.length !== rawDetections.length) {
    log.info('finalizeDetection3D.filter', 'Filtered low-fit detections', {
      minInlierRatio,
      beforeCount: rawDetections.length,
      afterCount: keptDetections.length,
    });
  }

  // Step 5: Convert Detection3D results to LocateResult[]
  const blobsAndPoses = keptDetections
    .map((detection) => {
      if (!detection.pose || !detection.blob2d) {
        return undefined;
      }

      let pose: CartesianPose = {
        x: detection.pose.x,
        y: detection.pose.y,
        z: detection.pose.z,
        i: detection.pose.i,
        j: detection.pose.j,
        k: detection.pose.k,
        w: detection.pose.w,
      };

      if (args.accuracyCalibration) {
        const cameraCorrection = getCalibrationCorrection(
          pose,
          args.accuracyCalibration,
        );

        pose = applyCompoundPose(pose, cameraCorrection);
      }

      // The offset always rides in the detected pose's own display frame
      // (vision-bot already encodes the grasp orientation in the pose).
      const effectivePlane = poseToDisplayFrame(pose);

      if (args.transform) {
        pose = applyUserDefinedTransform(args.transform, pose, effectivePlane);
      }

      return {
        // The result carries the detector BOX (Detection2D). Detection3D.blob2d
        // is now a box-only Detection2D — no contour is threaded this far.
        blob: {
          x: detection.blob2d.x,
          y: detection.blob2d.y,
          width: detection.blob2d.width,
          height: detection.blob2d.height,
          rotation: detection.blob2d.rotation,
          score: detection.blob2d.score,
          classId: detection.blob2d.classId,
          className: detection.blob2d.className,
        },
        pose,
        extents: detection.extents
          ? {
              x: detection.extents.x,
              y: detection.extents.y,
              z: detection.extents.z,
            }
          : undefined,
        segment: protoSegmentToBlob2D(detection.segment),
      };
    })
    .filter(isNotUndefined);

  // Apply result ordering and limit (consistent with regular locate path).
  // sortBlobs operates on LocateMatch; wrap each result's box as a segment-less
  // match for the comparators (no contour exists post-pose-estimation).
  const sortedBlobs = sortBlobs(
    blobsAndPoses.map((item) => ({ detection: item.blob, segment: null })),
    args.resultOrdering,
  );

  const sortedResults = sortedBlobs
    .map((match) => blobsAndPoses.find((item) => item.blob === match.detection))
    .filter(isNotUndefined)
    .slice(0, args.resultsLimit ?? blobsAndPoses.length);

  log.info(
    'finalizeDetection3D.transformed',
    'Transformed 3D blobs',
    sortedResults,
  );

  return {
    results: sortedResults,
    image,
    debugVisualization,
  };
}

/**
 * 3D locate path: text prompt → masks → depth → OBB pose estimation.
 * Bypasses plane projection entirely; uses Run3DPoseEstimation RPC.
 *
 * When a GPU Task Runner is available, uses SAM2 for precise segmentation masks.
 * Without a GPU, falls back to rectangular bounding-box masks and calls
 * vision-bot directly (GroundingDINO runs on CPU).
 */
async function run3DLocate(args: RunLocateArgs): Promise<{
  results: LocateResult[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
}> {
  if (args.method.kind !== 'Detection3D') {
    throw new Error('run3DLocate requires Detection3D method');
  }

  const { settings } = args.method;

  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for 3D locate');
  }

  if (!args.depthImage) {
    throw new Error('Depth image is required for 3D locate');
  }

  // Step 1: Get color frame
  const image = await getCameraColorFrame(
    args.camera.equipment,
    args.camera.integration,
  );

  // Step 2: Run 2D detection — GPU path uses SAM2 masks, CPU path uses bbox masks
  let detections: Run3DPoseEstimationRequest['detections'];

  // The CPU path fetches intrinsics for the rect-mask fallback; reuse that same
  // value for buildCameraParams (Step 3) instead of fetching twice. The GPU
  // path leaves this undefined and lets buildCameraParams fetch internally.
  let cpuIntrinsics: CameraIntrinsics | CameraIntrinsicsExtended | undefined;

  if (args.gpuTaskRunner) {
    // GPU path: SAM2 segmentation masks via GPU Task Runner
    const { croppedImage, offsetX, offsetY } =
      await args.vision.preprocessImage(image, args.regionOfInterest);

    const base64Image = Buffer.from(croppedImage).toString('base64');

    const inferenceResponse = await args.gpuTaskRunner.executeVisionCommand({
      command: 'run_inference',
      image: base64Image,
      model_path: 'text_prompt',
      model_type: 'TextPrompt',
      thresholding_params: {
        prompt: settings.textPrompt,
        use_sam: 'true',
        box_threshold: settings.boxThreshold.toString(),
        text_threshold: settings.textThreshold.toString(),
      },
      include_masks: true,
    });

    if (!inferenceResponse || typeof inferenceResponse === 'undefined') {
      throw new Error('2D detection returned no response');
    }

    const response2D = inferenceResponse as ProtoInferenceResponse;

    if (response2D.error) {
      throw new Error(`2D detection failed: ${response2D.error.message}`);
    }

    if (!response2D.data) {
      throw new Error('2D detection returned no data');
    }

    const protoBlobs = response2D.data.blobs;

    log.info(
      'run3DLocate.2d.gpu',
      `2D detection found ${protoBlobs.length} blobs`,
      { processingTimeMs: response2D.data.processingTimeMs },
    );

    // Adjust match coordinates for ROI offset: shift the detector box center
    // and (when present) every segment contour point back to full-frame space.
    // Inlined rather than shiftMatchByOffset: this is the proto-typed
    // Run3DPoseEstimationRequest detection where detection/segment may be
    // undefined off the wire (the zod LocateMatch requires detection).
    detections = protoBlobs.map((match) => ({
      ...match,
      detection: match.detection
        ? {
            ...match.detection,
            x: match.detection.x + offsetX,
            y: match.detection.y + offsetY,
          }
        : undefined,
      segment: match.segment
        ? {
            ...match.segment,
            contour: match.segment.contour.map((p) => ({
              x: p.x + offsetX,
              y: p.y + offsetY,
            })),
          }
        : undefined,
    }));
  } else {
    // CPU path: vision-bot direct (GroundingDINO + SAM2 on CPU)
    log.info(
      'run3DLocate.2d.cpu',
      'No GPU Task Runner, using vision-bot directly with SAM2 masks',
    );

    const blobs = await args.vision.detect2DTextPrompt(
      image,
      args.regionOfInterest,
      {
        gpuTaskRunnerId: '',
        textInput: settings.textPrompt,
        // Detection3DParams still carries separate box/text thresholds;
        // collapse via Math.max so the effective filter matches the
        // pre-nuke behavior where a detection had to clear both thresholds
        // independently (effective filter was score >= max(box, text)).
        // Dual thresholds come back in the multi-class follow-up PR.
        confidenceThreshold: Math.max(
          settings.boxThreshold,
          settings.textThreshold,
        ),
        useSam: true,
      },
    );

    log.info('run3DLocate.2d.cpu', `2D detection found ${blobs.length} blobs`);

    // Need image dimensions for rect mask fallback
    cpuIntrinsics = await getIntrinsics(args.intrinsics, args.camera.equipment);

    // Convert to proto Blob2D format; use SAM2 masks when available,
    // fall back to rectangular bbox masks if SAM2 didn't return masks.
    detections = blobsToDetectionsWithMasks(
      blobs,
      cpuIntrinsics.width,
      cpuIntrinsics.height,
    );
  }

  if (detections.length === 0) {
    return { results: [], image };
  }

  // Step 3: Get camera params (reuse the CPU-path intrinsics when present)
  const cameraParams = await buildCameraParams(args, cpuIntrinsics);

  // Step 4: Build and send 3D pose estimation request (shared with the
  // LocateSkill 3D-detection path via estimate3DPosesFromBlobs).
  const { detections: rawDetections, debugVisualization } =
    await estimate3DPosesFromBlobs({
      blobs: detections,
      depthImage: args.depthImage,
      cameraParams,
      config: settings,
      vision: args.vision,
      includeDebugVisualization: args.includeDebugVisualization,
    });

  // Step 4.5 + Step 5: filter low-fit detections, apply calibration +
  // user transform, convert to LocateResult[], order and limit. Shared with
  // the 2D-method 3D-detection projection path.
  return finalizeDetection3DResults({
    rawDetections,
    image,
    debugVisualization,
    config: settings,
    args,
  });
}

/**
 * 2D-method 3D-detection projection path: run the skill's own 2D detection
 * method (Blob/Shape/TextPrompt/CustomCheckpoint/DinoFast), then estimate a
 * 6D pose per detection from depth via the Detection3D backend — instead of
 * projecting blobs onto the taught plane.
 *
 * Reuses the same camera-param construction, mask fallback, pose estimation,
 * and post-processing as the Detection3D method path.
 */
async function run2DMethodWith3DProjection(args: RunLocateArgs): Promise<{
  results: LocateResult[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
}> {
  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for 3D-detection projection');
  }

  if (!args.depthImage) {
    throw new Error('Depth image is required for 3D-detection projection');
  }

  if (!args.detection3dConfig) {
    throw new Error(
      'detection3dConfig is required for 3D-detection projection',
    );
  }

  const { blobs, image } = await get2DBlobs(args);

  if (blobs.length === 0) {
    return { results: [], image };
  }

  // Need image dimensions for the rectangular mask fallback.
  const intrinsics = await getIntrinsics(
    args.intrinsics,
    args.camera.equipment,
  );

  const detections = blobsToDetectionsWithMasks(
    blobs,
    intrinsics.width,
    intrinsics.height,
  );

  if (detections.length === 0) {
    return { results: [], image };
  }

  // Reuse the intrinsics already fetched for the mask dims (no second round-trip).
  const cameraParams = await buildCameraParams(args, intrinsics);

  const { detections: rawDetections, debugVisualization } =
    await estimate3DPosesFromBlobs({
      blobs: detections,
      depthImage: args.depthImage,
      cameraParams,
      config: args.detection3dConfig,
      vision: args.vision,
      includeDebugVisualization: args.includeDebugVisualization,
    });

  return finalizeDetection3DResults({
    rawDetections,
    image,
    debugVisualization,
    config: args.detection3dConfig,
    args,
  });
}

export async function runLocate(args: RunLocateArgs): Promise<{
  results: LocateResult[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
  // DinoFast preview only (set when args.returnHeatmap): the dense score
  // heatmap from the 2D-detection pass. The 3D pipelines below don't surface
  // it — ClickFind in the routine editor uses the Manual plane path here.
  heatmap?: DinoFastDenseHeatmap | null;
}> {
  // 3D detection has its own pipeline (2D + masks → 3D pose estimation)
  if (args.method.kind === 'Detection3D') {
    return run3DLocate(args);
  }

  // 2D method + 3D-detection projection: run the chosen 2D method, then
  // estimate a 6D pose per detection from depth instead of plane projection.
  if (args.projectionMode === 'Detection3D') {
    return run2DMethodWith3DProjection(args);
  }

  const { blobs: blobs2D, image, heatmap } = await get2DBlobs(args);

  let cameraCorrection = ZERO_POSE;

  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for runLocate');
  }

  const intrinsics = await getIntrinsics(
    args.intrinsics,
    args.camera.equipment,
  );

  const extrinsics = await resolveVisionExtrinsics(
    args.camera.equipment,
    args.extrinsics,
  );

  log.info(
    'blobs',
    'Blobs detected',
    blobs2D.map((item) => ({
      ...item,
      segment: item.segment ? { ...item.segment, contour: undefined } : null,
    })),
  );

  const sortedBlobs2D = sortBlobs(blobs2D, args.resultOrdering);

  const filteredBlobs2D = sortedBlobs2D.slice(
    0,
    args.resultsLimit ?? sortedBlobs2D.length,
  );

  if (filteredBlobs2D.length === 0) {
    return { results: [], image, heatmap };
  }

  const mode = args.planeDetectionMode ?? 'Manual';

  log.debug('runLocate.mode', 'runLocate plane detection mode', {
    mode,
    rawPlaneDetectionMode: args.planeDetectionMode,
    hasPlane: !!args.plane,
    hasGpuTaskRunner: !!args.gpuTaskRunner,
    hasDepthImage: !!args.depthImage,
    depthImageBytes: args.depthImage?.byteLength ?? 0,
    includeDebugVisualization: args.includeDebugVisualization,
    blobCount: filteredBlobs2D.length,
  });

  log.info(
    'Accuracy calibration',
    'Accuracy calibration',
    args.accuracyCalibration,
  );

  // Legacy plane-frame reference for the user-defined offset, used only when no
  // grasp frame was explicitly chosen (the standalone non-skill Locate step).
  // Manual mode: the flat taught-plane pose. Geometric/Learned: base frame
  // (falls through to poseToDisplayFrame below). Preserves saved-routine grasp
  // points from before vision-bot resolved the grasp orientation.
  const planePoseRelativeToBase: CartesianPose =
    mode === 'Manual' && args.plane
      ? getPlanePose(movePlaneInItsZDirection(args.plane, args.objectHeight))
      : ZERO_POSE;

  const planeMethod = buildPlaneMethod(
    mode,
    args.plane,
    args.objectHeight,
    args.ransacParams,
    args.maxTiltAngle,
  );

  const request = buildPlaneProjectionRequest(
    filteredBlobs2D,
    image,
    intrinsics,
    extrinsics,
    planeMethod,
    args.getState,
    args.graspFrame,
    args.orientationReference,
    args.depthImage,
    args.includeDebugVisualization,
  );

  log.debug('planeProjection.request', 'Sending plane projection request', {
    planeMethodKey: planeMethod ? Object.keys(planeMethod)[0] : undefined,
    detectionCount: request.detections.length,
    hasDepthImage: !!request.depthImage?.length,
    depthImageBytes: request.depthImage?.length ?? 0,
    includeDebugVisualization: request.includeDebugVisualization,
  });

  let response;

  if (args.gpuTaskRunner) {
    response = await args.gpuTaskRunner.executePlaneProjection(request);
  } else {
    response = await args.vision.runPlaneProjection(request);
  }

  if (response.error) {
    throw new Error(`Plane projection failed: ${response.error.message}`);
  }

  if (!response.data) {
    throw new Error('Plane projection returned no data');
  }

  log.info('planeProjection', 'Plane projection completed', {
    detectionCount: response.data.detections.length,
    processingTimeMs: response.data.processingTimeMs,
    hasDebugVisualization: !!response.data.debugVisualization,
    debugVisualizationBytes: response.data.debugVisualization?.length ?? 0,
  });

  // Tilt filtering is handled by vision-bot (via maxTiltAngle in GeometricPlane proto)
  // so the debug visualization already excludes rejected detections.
  const { detections } = response.data;

  const blobsAndPoses = detections
    .map((detection) => {
      if (!detection.pose || !detection.blob2d) {
        return undefined;
      }

      let pose: CartesianPose = {
        x: detection.pose.x,
        y: detection.pose.y,
        z: detection.pose.z,
        i: detection.pose.i,
        j: detection.pose.j,
        k: detection.pose.k,
        w: detection.pose.w,
      };

      if (args.accuracyCalibration) {
        cameraCorrection = getCalibrationCorrection(
          pose,
          args.accuracyCalibration,
        );

        pose = applyCompoundPose(pose, cameraCorrection);
      }

      // When plane orientation is disabled, keep the RANSAC-derived position
      // but replace orientation with a straight-down approach aligned to
      // the base frame (display X=baseX, Y=baseY, Z=baseZ up).
      if (args.disablePlaneOrientation && mode !== 'Manual') {
        pose = { ...pose, i: 0.5, j: 0.5, k: -0.5, w: 0.5 };
      }

      // Reference frame the user-defined offset rides in:
      //  - graspFrame set (the skill flow always sets it): the resolved
      //    grasp-pose frame — vision-bot already encodes the grasp orientation
      //    in the pose, so the offset rides in the pose's own display frame.
      //  - graspFrame unset (legacy standalone Locate step): the flat taught-
      //    plane pose frame for Manual mode (poseToDisplayFrame otherwise), to
      //    preserve saved-routine grasp points from before grasp-frame resolution.
      const useLegacyPlaneFrame =
        args.graspFrame === undefined && mode === 'Manual';

      const effectivePlane = useLegacyPlaneFrame
        ? planePoseRelativeToBase
        : poseToDisplayFrame(pose);

      if (args.transform) {
        pose = applyUserDefinedTransform(args.transform, pose, effectivePlane);
      }

      return {
        // The result carries the detector BOX (Detection2D) and, when
        // segmentation ran, the SAM2 contour echoed back via Detection3D.segment.
        blob: {
          x: detection.blob2d.x,
          y: detection.blob2d.y,
          width: detection.blob2d.width,
          height: detection.blob2d.height,
          rotation: detection.blob2d.rotation,
          score: detection.blob2d.score,
          classId: detection.blob2d.classId,
          className: detection.blob2d.className,
        },
        pose,
        segment: protoSegmentToBlob2D(detection.segment),
      };
    })
    .filter(isNotUndefined);

  log.info('blobs.transformed', 'Transformed blobs', blobsAndPoses);

  return {
    results: blobsAndPoses,
    image,
    debugVisualization: response.data.debugVisualization,
    heatmap,
  };
}
